import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';

import { isTextColorRole } from '../tokens';
import type { TextColorRole } from '../tokens';

type ColorRoleFieldProps = {
  field: { label?: string };
  name: string;
  /**
   * Undefined when the prop is absent from saved page data — a component may make its colour
   * optional so it can fall back to another colour for pages authored before the prop existed
   * (see faq.tsx's headingColor). Treated as "no role selected", i.e. custom.
   */
  value: string | undefined;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

const ROLE_LABELS: Record<TextColorRole, string> = {
  text: 'Text',
  secondary: 'Secondary',
  muted: 'Muted',
  inverted: 'Inverted',
  link: 'Link',
};

const ROLE_KEYS = Object.keys(ROLE_LABELS) as TextColorRole[];

/** Starting colour when switching from a theme role to a custom value. */
const CUSTOM_DEFAULT = '#121516';

const COMPLETE_HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Constrains free-typed input to hex characters.
 *
 * Whatever is typed here is persisted verbatim as the colour, so without this a paste
 * of arbitrary text would be stored and then emitted as a CSS value. Partial input is
 * still allowed so the field can be typed into a digit at a time.
 *
 * Shared with background-field.tsx so both role pickers apply the same sanitisation.
 */
export function sanitizeHexInput(raw: string): string {
  return `#${raw.replace(/[^0-9a-fA-F]/g, '').slice(0, 6)}`;
}

/**
 * Shared with background-field.tsx so both role pickers stay visually identical.
 */
export const segmentStyle = (selected: boolean): React.CSSProperties => ({
  flex: 1,
  padding: '6px 4px',
  border: `1px solid ${selected ? '#2563eb' : '#d0d4d8'}`,
  borderRadius: 4,
  background: selected ? '#eff4ff' : '#fff',
  color: selected ? '#1d4ed8' : '#485055',
  fontSize: 12,
  fontWeight: selected ? 600 : 400,
  cursor: 'pointer',
});

/**
 * Puck custom field for text colour, offering the theme's semantic colour roles plus
 * a custom hex escape hatch. Roles are stored by key so the rendered colour keeps
 * tracking merchant branding; a custom value is stored as the literal hex.
 */
export function ColorRoleField({ field, name, value = '', onChange, readOnly }: ColorRoleFieldProps): JSX.Element {
  const isCustom = !isTextColorRole(value);

  const handleSelectCustom = useCallback(() => {
    if (!isCustom) onChange(CUSTOM_DEFAULT);
  }, [isCustom, onChange]);

  const handleHexChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange(sanitizeHexInput(e.currentTarget.value)),
    [onChange]
  );

  return (
    <FieldLabel label={field.label ?? name} readOnly={readOnly}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {ROLE_KEYS.map((role) => (
          <button
            key={role}
            type='button'
            disabled={readOnly}
            aria-pressed={value === role}
            onClick={() => onChange(role)}
            data-testid={`color-role-${name}-${role}`}
            style={segmentStyle(value === role)}
          >
            {ROLE_LABELS[role]}
          </button>
        ))}
        <button
          type='button'
          disabled={readOnly}
          aria-pressed={isCustom}
          onClick={handleSelectCustom}
          data-testid={`color-role-${name}-custom`}
          style={segmentStyle(isCustom)}
        >
          Custom
        </button>
      </div>

      {isCustom && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
          <input
            type='color'
            value={COMPLETE_HEX.test(value) ? value : CUSTOM_DEFAULT}
            onChange={handleHexChange}
            disabled={readOnly}
            aria-label={`${field.label ?? name} custom colour swatch`}
            data-testid={`color-role-swatch-${name}`}
            style={{
              width: 32,
              height: 32,
              padding: 0,
              border: '1px solid #d0d4d8',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          />
          <input
            type='text'
            value={value}
            onChange={handleHexChange}
            disabled={readOnly}
            aria-label={`${field.label ?? name} custom colour value`}
            data-testid={`color-role-hex-${name}`}
            style={{
              flex: 1,
              padding: '6px 8px',
              border: '1px solid #d0d4d8',
              borderRadius: 4,
              fontSize: 14,
              fontFamily: 'monospace',
            }}
          />
        </div>
      )}
    </FieldLabel>
  );
}
